import React from 'react';
import { motion } from 'framer-motion';
import { Delete, KeyRound, Lock } from 'lucide-react';
import { Button } from '../ui/Button/Button';
import { Field } from './ui/Field';
import { PIN } from '../../lib/rcu/catalog/security';
import {
  isPinGateSuccess,
  LOCKOUT_TICK_MS,
  PIN_GATE_MESSAGE,
  PIN_GATE_OUTCOME,
  SECONDS_PER_MINUTE,
} from '../../lib/technician/catalog';
import type { PinGateOutcome } from '../../lib/technician/catalog';
import { lockoutRemainingMs } from '../../lib/technician/state';
import { useRcuConfigStore } from '../../stores/rcuConfigStore';
import { useTechnicianStore } from '../../stores/technicianStore';

const KEYPAD = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'back'] as const;
type KeypadKey = (typeof KEYPAD)[number];

const clearKey: KeypadKey = 'clear';
const backKey: KeypadKey = 'back';

const secondsLeft = (ms: number): number => Math.ceil(ms / LOCKOUT_TICK_MS);

const formatCountdown = (ms: number): string => {
  const total = secondsLeft(ms);
  const minutes = Math.floor(total / SECONDS_PER_MINUTE);
  const seconds = total % SECONDS_PER_MINUTE;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

/** Four-digit gate in front of the technician console. */
export const PinGate: React.FC = () => {
  const technicianState = useTechnicianStore((state) => state.state);
  const unlock = useTechnicianStore((state) => state.unlock);
  const resetPin = useTechnicianStore((state) => state.resetPin);
  const deviceId = useRcuConfigStore((state) => state.config.rcu.deviceId);

  const [pin, setPin] = React.useState('');
  const [outcome, setOutcome] = React.useState<PinGateOutcome | null>(null);
  const [attemptsLeft, setAttemptsLeft] = React.useState<number>(PIN.MAX_ATTEMPTS);
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  const [recovering, setRecovering] = React.useState(false);
  const [deviceIdInput, setDeviceIdInput] = React.useState('');

  const remainingMs = lockoutRemainingMs(technicianState, nowMs);
  const lockedOut = remainingMs > 0;

  // Tick only while a cool-down is running.
  React.useEffect(() => {
    if (remainingMs <= 0) return undefined;
    const timer = setInterval(() => setNowMs(Date.now()), LOCKOUT_TICK_MS);
    return () => clearInterval(timer);
  }, [remainingMs]);

  const submit = (candidate: string): void => {
    const attempt = unlock(candidate);
    setOutcome(attempt.outcome);
    setAttemptsLeft(attempt.attemptsRemaining);
    setPin('');
  };

  const press = (key: KeypadKey): void => {
    if (lockedOut) return;

    if (key === clearKey) {
      setPin('');
      setOutcome(null);
      return;
    }
    if (key === backKey) {
      setPin((current) => current.slice(0, -1));
      return;
    }

    const next = `${pin}${key}`.slice(0, PIN.LENGTH);
    setPin(next);
    setOutcome(null);
    if (next.length === PIN.LENGTH) submit(next);
  };

  const handleRecover = (): void => {
    const result = resetPin(deviceIdInput, deviceId);
    setOutcome(result);
    if (result === PIN_GATE_OUTCOME.PIN_RESET) {
      setRecovering(false);
      setDeviceIdInput('');
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#1A1A1A] px-6 py-10">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="w-full max-w-sm rounded-3xl bg-white p-7 shadow-[0_24px_48px_rgba(0,0,0,0.35)]"
      >
        <div className="mb-6 text-center">
          <span className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#FDF6E9] text-[#D4A054]">
            {recovering ? <KeyRound className="h-6 w-6" /> : <Lock className="h-6 w-6" />}
          </span>
          <h1 className="font-display text-2xl text-[#333333]">
            {recovering ? 'Recover PIN' : 'Technician access'}
          </h1>
          <p className="mt-1 text-sm text-[#666666]">
            {recovering
              ? 'Type this RCU\u2019s device ID to reset the PIN to 0000.'
              : 'Enter the four-digit console PIN.'}
          </p>
        </div>

        {recovering ? (
          <div className="space-y-4">
            <Field
              label="Device ID"
              value={deviceIdInput}
              onChange={setDeviceIdInput}
              placeholder="TYM-RCU-0001"
              autoFocus
            />
            <div className="flex gap-3">
              <Button variant="secondary" onClick={() => setRecovering(false)} className="flex-1">
                Back
              </Button>
              <Button
                variant="primary"
                onClick={handleRecover}
                disabled={deviceIdInput.trim() === ''}
                className="flex-1"
              >
                Reset PIN
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-5 flex justify-center gap-3">
              {Array.from({ length: PIN.LENGTH }, (_unused, index) => (
                <span
                  key={index}
                  className={`flex h-12 w-11 items-center justify-center rounded-xl border text-2xl font-medium ${
                    index < pin.length
                      ? 'border-[#D4A054] bg-[#FDF6E9] text-[#D4A054]'
                      : 'border-[#E0E0E0] bg-white text-[#CCCCCC]'
                  }`}
                >
                  {index < pin.length ? '\u2022' : ''}
                </span>
              ))}
            </div>

            <div className="mb-4 grid grid-cols-3 gap-2">
              {KEYPAD.map((key) => (
                <button
                  key={key}
                  type="button"
                  disabled={lockedOut}
                  onClick={() => press(key)}
                  className="flex h-14 items-center justify-center rounded-2xl bg-[#F5F5F5] text-xl font-medium text-[#333333] transition-colors hover:bg-[#E8C078]/40 disabled:opacity-40"
                >
                  {key === backKey ? (
                    <Delete className="h-5 w-5" />
                  ) : key === clearKey ? (
                    <span className="text-sm text-[#666666]">Clear</span>
                  ) : (
                    key
                  )}
                </button>
              ))}
            </div>

            <div className="min-h-[44px] text-center text-sm">
              {lockedOut ? (
                <p className="text-[#C62828]">
                  Locked for {formatCountdown(remainingMs)} after too many attempts.
                </p>
              ) : outcome === null ? (
                <p className="text-[#999999]">
                  {attemptsLeft} of {PIN.MAX_ATTEMPTS} attempts left
                </p>
              ) : (
                <p className={isPinGateSuccess(outcome) ? 'text-[#2E7D32]' : 'text-[#C62828]'}>
                  {PIN_GATE_MESSAGE[outcome]}
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={() => setRecovering(true)}
              className="mx-auto mt-2 block text-xs font-medium text-[#D4A054] hover:text-[#B8893F]"
            >
              Forgot PIN?
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default PinGate;
